import ApiService from '@/app/infrastructures/services/ApiService'
import { PickupRepositoryInterface } from '@/data/persistences/repositories/contracts/PickupRepositoryInterface'
import { Endpoints } from '@/app/infrastructures/misc/Endpoints'
import { PickupMapper } from '@/data/persistences/mappers/PickupMapper'
import { ManageCourierMapper } from '@/data/persistences/mappers/ManageCourierMapper'
import { ManageCouriers } from '@/domain/entities/ManageCourier'
import { DashboardPickupTransferOutcome, NearbyCourier, PickupAdditionalTimes, PickupCorporates, PickupCorporateSummary, PickupCRRNFDSummary, PickupCRROTWV2List, PickupCRROTWV2Summary, PickupCRRQUEs, PickupDetail, PickupDetailSnapshot, PickupForceDetail, PickupNfdList, PickupNfdShipperHashDetail, PickupNfdSummary, PickupShipperHashOutcome, Pickups, PickupSummary, PickupCRROTWs, AssignPickupBulkHistoryList, AdminContactShow, PickupCorporatesV2, PickupCorporateNfdDeadlineSummary } from '@/domain/entities/Pickup'
import {
  AdminTaskPickupCancelRequestInterface,
  AdminTaskPickupDoneRequestInterface,
  CancelPickupRequestInterface,
  ExportPickupRequestInterface,
  ManualAssigneeBulkRequestInterface,
  ManualAssigneeRequestInterface,
  MarkAsDoneRequestInterface,
  PickupDedicatedCRROTWRequestInterface,
  PickupForceCRRCNCRequestInterface,
  ReadAssignPickupBulkHistoryRequestInterface,
} from '@/data/payload/contracts/PickupRequest'
import { DashboardPickupTransferRequest, ExportPickupRequest, ForceCancelCRRQUERequest, ForceCRRSRCRequest, UpdateIsContactedCRROTWRequest, UpdateIsContactedCRRQUERequest } from '@/data/payload/api/PickupRequest'
import { AxiosResponse } from 'axios'
import { Utils } from '../../misc'
import { Report } from '@/domain/entities/Main'

export class PickupApiRepository implements PickupRepositoryInterface {
  private service: ApiService
  private mapper: PickupMapper
  private manageCourierMapper: ManageCourierMapper

  constructor(
    service: ApiService,
    mapper: PickupMapper,
    manageCourierMapper: ManageCourierMapper
  ) {
    this.service = service
    this.mapper = mapper
    this.manageCourierMapper = manageCourierMapper
  }

  public async getListCRROTW(params: Map<string, string | number>): Promise<PickupCRROTWs> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.PICKUP_OTW,
      params
    )
    return this.mapper.convertPickupCRROTWsFromApi(resp)
  }

  public async getListCRROTWV2(
    params: Map<string, string | number>
  ): Promise<PickupCRROTWV2List> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.PICKUP_OTW_V2,
      Object.fromEntries(params) as unknown as Map<string, string | number>
    )
    return this.mapper.convertPickupCRROTWV2ListFromApi(resp)
  }

  public async getSummaryCRROTWV2(
    params: Map<string, string | number>
  ): Promise<PickupCRROTWV2Summary> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.PICKUP_OTW_V2_SUMMARY,
      Object.fromEntries(params) as unknown as Map<string, string | number>
    )
    return this.mapper.convertPickupCRROTWV2SummaryFromApi(resp)
  }

  public async updateIsContactedCRROTW(taskId: number, payload: UpdateIsContactedCRROTWRequest): Promise<boolean> {
    await this.service.invoke(
      'put',
      `${Endpoints.Route.PICKUP_OTW_V2}/${taskId}`,
      undefined,
      payload
    )
    return true
  }

  public async getAll(params: Map<string, string | number>): Promise<Pickups> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.PICKUP_V2,
      params
    )
    return this.mapper.convertPickupsFromApi(resp)
  }

  public async get(params: { shipmentId: string; isAllData?: boolean }): Promise<PickupDetail> {
    const resp = await this.service.invoke(
      'get',
      `${Endpoints.Route.PICKUP}/${params.shipmentId}?is_show_detail=${params.isAllData ?? false}`
    )
    return this.mapper.convertPickupFromApi(resp)
  }

  public async getBulk(params: Map<string, string | number>): Promise<PickupDetail[]> {
    const resp = await this.service.invoke(
      'get',
      `${Endpoints.Route.PICKUP}/bulk`,
      params
    )

    return this.mapper.convertPickupBulkFromApi(resp)
  }

  public async getListCRRNFD(params: Map<string, string | number>): Promise<Pickups> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.PICKUP_V2,
      params
    )
    return this.mapper.convertPickupsFromApi(resp)
  }

  public async getPickupNfdList(
    params: Map<string, string | number>
  ): Promise<PickupNfdList> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.PICKUP_ADMIN_NFD,
      params
    )
    return this.mapper.convertPickupNfdListFromApi(resp)
  }

  public async getPickupNfdSummary(
    params: Map<string, string | number>
  ): Promise<PickupNfdSummary> {
    const resp = await this.service.invoke(
      'get',
      `${Endpoints.Route.PICKUP_ADMIN_NFD}/summary`,
      params
    )
    return this.mapper.convertPickupNfdSummaryFromApi(resp)
  }

  public async getPickupNfdByShipperHash(
    shipperHash: string,
    params: Map<string, string | number>
  ): Promise<PickupNfdShipperHashDetail> {
    const encoded = encodeURIComponent(String(shipperHash || '').trim())
    const resp = await this.service.invoke(
      'get',
      `${Endpoints.Route.PICKUP_ADMIN_NFD}/${encoded}`,
      params
    )
    return this.mapper.convertPickupNfdShipperHashDetailFromApi(
      resp,
      shipperHash
    )
  }

  public async postPickupNfdShipperHashToCrrsrc(
    shipperHashes: string[]
  ): Promise<PickupShipperHashOutcome[]> {
    const resp = await this.service.invoke(
      'post',
      Endpoints.Route.PICKUP_NFD_SHIPPER_HASH,
      undefined,
      {
        toPayload: () => {
          return JSON.stringify({
            shipper_hashes: shipperHashes
          })
        }
      },
      new Map([
        [
          'metadata',
          Utils.convertBase64(`{"timezone": "${Utils.getIANATimeZone()}"}`),
        ],
      ])
    )
    return this.mapper.convertPickupShipperHashOutcomesFromApi(resp)
  }

  public async manualAssignPickupBulkV2(
    payload: ManualAssigneeBulkRequestInterface
  ): Promise<PickupShipperHashOutcome[]> {
    const resp = await this.service.invoke(
      'post',
      Endpoints.Route.MANUAL_ASSIGNEE_PICKUP_BULK_V2,
      undefined,
      payload,
      new Map([
        [
          'metadata',
          Utils.convertBase64(`{"timezone": "${Utils.getIANATimeZone()}"}`),
        ],
      ])
    )
    return this.mapper.convertBulkManualAssignV2OutcomesFromApi(resp)
  }

  public async getAssignPickupBulkHistory(
    params: Map<string, string | number>
  ): Promise<AssignPickupBulkHistoryList> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.ASSIGN_PICKUP_BULK_HISTORY,
      params
    )
    return this.mapper.convertAssignPickupBulkHistoryListFromApi(resp)
  }

  public async readAssignPickupBulkHistory(
    payload: ReadAssignPickupBulkHistoryRequestInterface
  ): Promise<boolean> {
    await this.service.invoke(
      'post',
      Endpoints.Route.ASSIGN_PICKUP_BULK_HISTORY_READ,
      undefined,
      payload
    )
    return true
  }

  public async getListCRRDON(params: Map<string, string | number>): Promise<Pickups> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.PICKUP,
      params
    )
    return this.mapper.convertPickupsFromApi(resp)
  }

  public async getPickupDetail(params: Map<string, string | number>): Promise<PickupForceDetail> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.PICKUP_FORCE_DETAIL,
      params
    )
    return this.mapper.convertPickupDetailFromApi(resp)
  }

  public async getSnapshot(shipmentId: string): Promise<PickupDetailSnapshot[]> {
    const resp = await this.service.invoke(
      'get', `${Endpoints.Route.SNAPSHOT}/${shipmentId}`
    )
    return this.mapper.convertSnapshotFromApi(resp)
  }

  public async getSummary(params: Map<string, string | undefined>): Promise<PickupSummary> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.PICKUP_SUMMARY,
      params
    )
    return this.mapper.convertPickupSummaryFromApi(resp)
  }

  public async export(
    payload: ExportPickupRequestInterface
  ): Promise<string[]> {
    const resp = await this.service.invoke(
      'post',
      Endpoints.Route.PICKUP_EXPORT,
      undefined,
      payload
    )
    return resp.data.data
  }

  public async exportCRRNFD(params: Map<string, string>): Promise<string> {
    const resp = await this.service.invoke(
      'get',
      `${Endpoints.Route.SHIPMENT_ADMIN}/pickup-crrnfd/export`,
      params,
    )

    return this.mapper.convertCRRNFDExportFromApi(resp)
  }

  public async getNearbyCourier(
    params: Map<string, number>
  ): Promise<NearbyCourier[]> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.NEARBY_COURIER,
      params
    )
    return this.mapper.convertNearbyCourierFromApi(resp)
  }

  public async manualAssignee(
    payload: ManualAssigneeRequestInterface
  ): Promise<any> {
    const res = await this.service.invoke(
      'post',
      `${Endpoints.Route.MANUAL_ASSIGNEE_PICKUP}/bulk`,
      undefined,
      payload,
      new Map([
        ['metadata', Utils.convertBase64(`{"timezone": "${Utils.getIANATimeZone()}"}`)]
      ])
    )

    if(res.status === 200 && res.data?.data !== null) {
      return res.data.data
    }

    return true
  }

  public async markAsDone(
    payload: MarkAsDoneRequestInterface,
    group: boolean
  ): Promise<boolean> {
    await this.service.invoke(
      'post',
      group
        ? Endpoints.Route.PICKUP_GROUP_MARK_AS_DONE
        : Endpoints.Route.PICKUP_MARK_AS_DONE,
      undefined,
      payload,
      new Map([
        ['metadata', Utils.convertBase64(`{"timezone": "${Utils.getIANATimeZone()}"}`)]
      ])
    )

    return true
  }

  public async adminTaskPickupDone(
    payload: AdminTaskPickupDoneRequestInterface
  ): Promise<boolean> {
    await this.service.invoke(
      'post',
      Endpoints.Route.PICKUP_ADMIN_TASK_PICKUP_DONE,
      undefined,
      payload,
      new Map([
        [
          'metadata',
          Utils.convertBase64(`{"timezone": "${Utils.getIANATimeZone()}"}`),
        ],
      ])
    )
    return true
  }

  public async adminTaskPickupCancel(
    payload: AdminTaskPickupCancelRequestInterface
  ): Promise<boolean> {
    await this.service.invoke(
      'post',
      Endpoints.Route.PICKUP_ADMIN_TASK_PICKUP_CANCEL,
      undefined,
      payload,
      new Map([
        [
          'metadata',
          Utils.convertBase64(`{"timezone": "${Utils.getIANATimeZone()}"}`),
        ],
      ])
    )
    return true
  }

  public async cancelPickup(
    payload: CancelPickupRequestInterface
  ): Promise<boolean> {
    await this.service.invoke(
      'post',
      Endpoints.Route.CANCEL_ORDER,
      undefined,
      payload,
      new Map([
        ['metadata', Utils.convertBase64(`{"timezone": "${Utils.getIANATimeZone()}"}`)]
      ])
    )

    return true
  }

  public async pickupDedicatedCRROTW(
    payload: PickupDedicatedCRROTWRequestInterface
  ): Promise<boolean> {
    await this.service.invoke(
      'post',
      Endpoints.Route.PICKUP_DEDICATED_CRROTW,
      undefined,
      payload,
      new Map([
        ['metadata', Utils.convertBase64(`{"timezone": "${Utils.getIANATimeZone()}"}`)]
      ])
    )
    return true
  }

  public async getPickupAdditionalTime(
    params: Map<string, string>
  ): Promise<PickupAdditionalTimes> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.PICKUP_ADDITIONAL_TIME,
      params
    )
    return this.mapper.convertPickupAdditionalTimeFromApi(resp)
  }

  public async exportPickupList(params: Map<string, string>): Promise<string> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.EXPORT_PICKUP_LIST,
      params,
    )

    return this.mapper.convertPickupListFromApi(resp)
  }

  public async forceCRRCNC(payload: PickupForceCRRCNCRequestInterface): Promise<boolean> {
    await this.service.invoke(
      'POST',
      Endpoints.Route.PICKUP_FORCE_CRRCNC,
      undefined,
      payload,
      new Map([
        ['metadata', Utils.convertBase64(`{"timezone": "${Utils.getIANATimeZone()}"}`)]
      ])
    )

    return true
  }

  public async exportCRROTW(params: Map<string, string>): Promise<string> {
    const resp = await this.service.invoke(
      'GET',
      `${Endpoints.Route.PICKUP_OTW}/export`,
      params
    )

    return this.mapper.convertCRROTWExportFromApi(resp)
  }

  public async forceCRRSRC(payload: ForceCRRSRCRequest): Promise<boolean> {
    await this.service.invoke(
      'POST',
      `${Endpoints.Route.SHIPMENT_PICKUP}/corporate`,
      undefined,
      payload,
      new Map([
        ['metadata', Utils.convertBase64(`{"timezone": "${Utils.getIANATimeZone()}"}`)]
      ])
    )

    return true
  }

  public async getListCRRQUE(params: Map<string, string | number>): Promise<PickupCRRQUEs> {
    const resp = await this.service.invoke(
      'GET',
      Endpoints.Route.PICKUP_QUEUE,
      params
    )

    return this.mapper.convertCRRQUEFromApi(<AxiosResponse>resp)
  }

  public async forceCancelCRRQUE(payload: ForceCancelCRRQUERequest): Promise<boolean> {
    await this.service.invoke(
      'POST',
      `${Endpoints.Route.PICKUP_QUEUE}/cancel`,
      undefined,
      payload,
      new Map([
        ['metadata', Utils.convertBase64(`{"timezone": "${Utils.getIANATimeZone()}"}`)]
      ])
    )

    return true
  }

  public async updateIsContactedCRRQUE(shipmentId: string, payload: UpdateIsContactedCRRQUERequest): Promise<boolean> {
    await this.service.invoke(
      'PUT',
      `${Endpoints.Route.PICKUP_QUEUE}/${shipmentId}`,
      undefined,
      payload
    )

    return true
  }

  public async exportCRRQUE(params: Map<string, string | number>): Promise<string> {
    const resp = await this.service.invoke(
      'GET',
      `${Endpoints.Route.PICKUP_QUEUE}/download`,
      params
    )

    return this.mapper.convertCRRQUEExportFromApi(resp)
  }

  public async getCRRNFDSummary(): Promise<PickupCRRNFDSummary> {
    const resp = await this.service.invoke(
      'GET',
      `${Endpoints.Route.PICKUP_NFD}/summary`
    )

    return this.mapper.convertCRRNFDSummaryFromApi(resp)
  }

  public async exportManualAssignLog(params: Map<string, string>): Promise<string> {
    const resp = await this.service.invoke(
      'GET',
      Endpoints.Route.MANUAL_ASSIGN_EXPORT_LOG,
      params
    )

    return this.mapper.convertManualAssignPickupFromApi(resp)
  }

  public async getListCorporate(params: Map<string, string | number>): Promise<PickupCorporates> {
    const resp = await this.service.invoke(
      'GET',
      `${Endpoints.Route.PICKUP_CORPORATE}/lists`,
      params
    )

    return this.mapper.convertPickupCorporatesFromApi(resp)
  }

  public async getListCorporateV2(params: Map<string, string | number>): Promise<PickupCorporatesV2> {
    const resp = await this.service.invoke(
      'GET',
      `${Endpoints.Route.PICKUP_CORPORATE}/lists`,
      params
    )

    return this.mapper.convertPickupCorporatesFromApiV2(resp)
  }

  public async getCorporateSummary(): Promise<PickupCorporateSummary> {
    const resp = await this.service.invoke(
      'GET',
      `${Endpoints.Route.PICKUP_CORPORATE}/summary`
    )

    return this.mapper.convertPickupCorporateSummaryFromApi(resp)
  }

  public async getExportPickup(payload: ExportPickupRequest): Promise<Report> {
    const resp = await this.service.invoke(
      payload.method,
      `${Endpoints.Route.SHIPMENT_ADMIN}${payload.route}`,
      payload.payload,
      payload
    )

    return this.mapper.convertExportPickupFromApi(resp);
  }

  public async getCouriersForDashboardPickupTransfer(
    params: Map<string, string | number>
  ): Promise<ManageCouriers> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.COURIER,
      Object.fromEntries(params) as unknown as Map<string, string | number>
    )
    return this.manageCourierMapper.convertManageCouriersFromApi(resp)
  }

  public async transferDashboardPickup(
    payload: DashboardPickupTransferRequest
  ): Promise<DashboardPickupTransferOutcome[]> {
    const resp = await this.service.invoke(
      'post',
      Endpoints.Route.PICKUP_DASHBOARD_TRANSFER,
      new Map<string, string | number>(),
      payload,
      new Map([
        [
          'metadata',
          Utils.convertBase64(`{"timezone": "${Utils.getIANATimeZone()}"}`),
        ],
      ])
    )
    return this.mapper.convertDashboardPickupTransferFromApi(resp)
  }

  public async adminContactShow(params: Map<string, string>): Promise<AdminContactShow> {
    const resp = await this.service.invoke(
      'GET',
      `${Endpoints.Route.ADMIN_CONTACT_SHOW}`,
      params
    )

    return this.mapper.convertAdminContactShowFromApi(resp)
  }

  public async syncPickupCorporate() {
    const resp = await this.service.invoke(
      "POST",
      `${Endpoints.Route.PICKUP_CORPORATE}/sync`,
    )

    return this.mapper.convertSyncPickupCorporateFromApi(resp);
  }

  public async getNfdDeadlineSummary(params: Map<string, string | number>): Promise<PickupCorporateNfdDeadlineSummary> {
    const resp = await this.service.invoke(
      'GET',
      `${Endpoints.Route.PICKUP_CORPORATE}/nfd-deadline-summary`,
      params
    )

    return this.mapper.convertPickupCorporatesNfdDeadlineSummaryFromApi(resp)
  }

  public async exportCorporate(params: Map<string, string | number | boolean>): Promise<Report> {
    const resp = await this.service.invoke(
      'get',
      `${Endpoints.Route.PICKUP_CORPORATE}/export`,
      params,
    )

    return this.mapper.convertExportCorporateFromApi(resp)
  }
}
